import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  ListItemIcon,
  Radio,
  Checkbox,
  Button,
} from "@mui/material";
import {
  TableBorderRadiusTopLeftRight,
  StyledTableCell,
  pageSize,
  NoDataContainer,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../utils/dataTableShell";
import http from "../../utils/http";
import { useNavigate, useParams } from "react-router-dom";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import DownloadIcon from "@mui/icons-material/Download";
import { FilterAlt, MoreVert } from "@mui/icons-material";
import VerifyConsentModal from "../ParticipantRecords/ParticipantDetails/ConsentForms/VerifyConsentModal";
import StatusBadge from "../Common/UI/StatusTag";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { permissions } from "../../utils/roles";
import {
  setTabCategoryType,
  setTabMenuType,
} from "../../Redux/reducers/participantTabSlice";

const statusMap: any = {
  in_progress: { label: "Inprogress", variant: "pending" },
  signed: { label: "Participant Signed", variant: "active" },
  not_started: { label: "Sent", variant: "sent" },
  provider_signed: { label: "Completed", variant: "sent" },
  verified: { label: "Verified", variant: "sent" },
};

type Props = {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  formType: "consent" | "sunshine";
};

const ConsentsTracking = ({
  page,
  setPage,
  filter,
  setFilter,
  formType,
}: Props) => {
  const { id: studyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<any>([]);
  const [dataCount, setDataCount] = useState(0);

  const [toggleLoader, setToggleLoader] = useState<boolean>(false);

  const [loading, setLoading] = useState(true);
  const [downloadingRowId, setDownloadingRowId] = useState<string>("");
  const [selectedParticipantId, setSelectedParticipantId] =
    useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [filterEl, setFilterEl] = useState<null | HTMLElement>(null);
  const openFilter = Boolean(filterEl);
  const [selectedConsents, setSelectedConsents] = useState<Set<string>>(
    new Set()
  );
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const dataEditable = useAppSelector((state) => state.study.dataEditable);
  const sitePermissions = useAppSelector(
    (state) => state.study.sitePermissions
  );

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let prefix = `/admin/study/${studyId}/econsent/participants/econsents`;
        if (formType === "sunshine") {
          prefix = `/admin/study/${studyId}/sunshine/participants/sunshine-forms`;
        }

        let url = `${prefix}?page=${page + 1}&size=${pageSize}`;
        if (filter) {
          url += `&status=${filter}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.data?.map((item: any, index: number) => {
          const siteId = item?.participant?.studyParticipant?.siteId;
          const editable =
            dataEditable &&
            sitePermissions?.[siteId || ""]?.permissions?.includes(
              permissions.editParticipant
            );
          return {
            id: item?.consentVersion?.id,
            name: item?.consentVersion?.consent?.name,
            participantName: item?.participant?.firstName
              ? item?.participant?.lastName
                ? `${item?.participant?.firstName} ${item?.participant?.lastName}`
                : item?.participant?.firstName
              : "",
            version: item?.consentVersion?.version,
            consentId: item?.id,
            consentDate: item?.signedOn
              ? DateTime.fromISO(item?.signedOn).toFormat("MM/dd/yyyy")
              : "-",
            status: item?.status,
            sentOn: item?.sentOn
              ? DateTime.fromISO(item?.sentOn).toFormat("MMM dd, yyyy")
              : "-",
            participantId: item?.participant?.id,
            subjectId: item?.participant?.studyParticipant?.subjectId,
            editable: editable,
          };
        });

        setData(newData || []);
        setDataCount(data?.meta?.paginationOptions?.totalItems || 0);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [
    page,
    studyId,
    toggleLoader,
    filter,
    sitePermissions,
    dataEditable,
    formType,
  ]);

  useEffect(() => {
    setSelectedConsents(new Set());
  }, [formType]);

  const handleDownload = async (participantId: string, versionId: string) => {
    try {
      setDownloadingRowId(versionId);
      setSelectedParticipantId(participantId);
      let prefix = `/admin/study/${studyId}/econsent/participants/${participantId}/signed-econsents`;
      if (formType === "sunshine") {
        prefix = `/admin/study/${studyId}/sunshine/participants/${participantId}/signed-sunshine-forms`;
      }
      let res = await http.get(`${prefix}/${versionId}/download`);
      const downloadLink = res?.data?.data?.downloadUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
      setSelectedParticipantId("");
    } catch (err) {
      setDownloadingRowId("");
      setSelectedParticipantId("");
      errorToastMessage(err as Error);
    }
  };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const resendConsent = async (participantId: string, versionId: string) => {
    try {
      setDownloadingRowId(versionId);
      setSelectedParticipantId(participantId);

      let body: any = {
        versionId: versionId,
      };
      let prefix = `/admin/study/${studyId}/econsent`;
      if (formType === "sunshine") {
        prefix = `/admin/study/${studyId}/sunshine`;
      }
      let res = await http.post(
        `${prefix}/participants/${participantId}/resend`,
        body
      );
      toastMessage("success", res.data.message);
      setDownloadingRowId("");
      setSelectedParticipantId("");
    } catch (err) {
      setDownloadingRowId("");
      setSelectedParticipantId("");
      errorToastMessage(err as Error);
    }
  };

  const handleSignConsent = (
    participantId: string,
    consentId: string,
    versionId: string,
    status: string
  ) => {
    if (status === "signed") {
      navigate(
        `/studies/${studyId}/participants/${participantId}/consent-form/${consentId}?versionId=${versionId}`
      );
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openVerifyModal = () => {
    setAnchorEl(null);
    setShowVerifyModal(true);
  };

  const closeVerifyModal = () => {
    setShowVerifyModal(false);
    setSelectedRow(null);
  };

  const openFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterEl(event.currentTarget);
  };

  const handleFilter = (value: string) => {
    setFilter(value);
    setFilterEl(null);
    setPage(0);
  };
  const clearFilter = () => {
    setFilter("");
    setFilterEl(null);
  };

  const closeFilterMenu = () => {
    setFilterEl(null);
  };

  const navigateToParticipantDetailsPage = (
    participantId: string,
    subjectId: string
  ) => {
    dispatch(setTabCategoryType("patient_profile"));
    dispatch(
      setTabMenuType(formType === "consent" ? "consent" : "sunshine_forms")
    );
    sessionStorage.setItem("category-type", "patient_profile");
    sessionStorage.setItem(
      "menu-type",
      formType === "consent" ? "consent" : "sunshine_forms"
    );
    navigate(
      `/studies/${studyId}/participants/${participantId}/details?participant=${subjectId}`
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedConsents((prev) => {
        const newConsentIds = data
          .filter(
            (item: any) =>
              item?.status === "signed" ||
              item?.status === "provider_signed" ||
              item?.status === "verified"
          )
          .map((item: any) => item.consentId);
        return new Set([...prev, ...newConsentIds]);
      });
    }
  };

  const handleSelectRow = (
    event: React.ChangeEvent<HTMLInputElement>,
    consentId: string
  ) => {
    if (event.target.checked) {
      setSelectedConsents((prev) => new Set([...prev, consentId]));
    } else {
      setSelectedConsents((prev) => {
        const newSet = new Set(prev);
        newSet.delete(consentId);
        return newSet;
      });
    }
  };

  const isRowSelected = (consentId: string) => {
    return selectedConsents.has(consentId);
  };

  const handleBulkDownload = async () => {
    if (selectedConsents.size === 0) {
      toastMessage("warning", "Please select at least one consent to download");
      return;
    }

    try {
      setBulkDownloadLoading(true);
      let prefix = `/admin/study/${studyId}/econsent/participants/econsents`;
      if (formType === "sunshine") {
        prefix = `/admin/study/${studyId}/sunshine/participants/sunshine-forms`;
      }

      const participantEconsentIds = Array.from(selectedConsents);

      const res = await http.post(
        `${prefix}/bulk-download`,
        { participantEconsentIds },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `consents-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastMessage(
        "success",
        `${selectedConsents.size} consent(s) downloaded successfully`
      );
      setSelectedConsents(new Set());
      setBulkDownloadLoading(false);
    } catch (err) {
      setBulkDownloadLoading(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <>
      <Box
        sx={{
          ...dataTableListShell,
          p: 2,
          backgroundColor: "#fff",
          borderRadius: "10px",
        }}
      >
        <DataTableLayout
          toolbar={
            selectedConsents.size > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="body1" fontWeight="medium">
                  {selectedConsents.size}{" "}
                  {formType === "consent" ? "consent" : "form"}(s) selected
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedConsents(new Set())}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      bulkDownloadLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={handleBulkDownload}
                    disabled={bulkDownloadLoading}
                  >
                    Download Selected
                  </Button>
                </Box>
              </Box>
            ) : undefined
          }
          footer={
            <DataTableFooter
              filteredCount={dataCount}
              page={page}
              pageSize={pageSize}
              onPageChange={handleChangePage}
              loading={loading}
            />
          }
        >
          <Table
            sx={TableBorderRadiusTopLeftRight}
            stickyHeader
            className="scrollable-table"
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox checked={false} onChange={handleSelectAll} />
                    <Typography fontWeight={"bold"} variant="body2">
                      Participant Name
                    </Typography>
                  </Box>
                </StyledTableCell>
                <StyledTableCell>
                  {formType === "consent" ? "Consent Name" : "Form Name"}
                </StyledTableCell>
                <StyledTableCell>
                  {formType === "consent"
                    ? "Provider Consent"
                    : "Provider Signature"}
                </StyledTableCell>
                <StyledTableCell>Version</StyledTableCell>
                <StyledTableCell>
                  {formType === "consent" ? "Consent Date" : "Form Date"}
                </StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
                <StyledTableCell align="right">
                  <IconButton onClick={openFilterMenu}>
                    <FilterAlt color={filter === "" ? "disabled" : "primary"} />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            {!loading && data?.length > 0 && (
              <TableBody>
                {data?.map((item: any, index: number) => {
                  const isSelectable =
                    item?.status === "signed" ||
                    item?.status === "provider_signed" ||
                    item?.status === "verified";
                  const isSelected = isRowSelected(item.consentId);

                  return (
                    <TableRow key={index}>
                      <StyledTableCell
                        onClick={() =>
                          navigateToParticipantDetailsPage(
                            item?.participantId,
                            item?.subjectId
                          )
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={!isSelectable}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              handleSelectRow(e, item.consentId)
                            }
                          />
                          <Typography
                            variant="body1"
                            fontWeight={"regular"}
                            sx={{ flexShrink: 0 }}
                          >
                            {item?.participantName || item?.subjectId}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>{item?.name}</StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          onClick={() => {
                            if (item.editable)
                              handleSignConsent(
                                item?.participantId,
                                item?.consentId,
                                item?.id,
                                item?.status
                              );
                          }}
                          sx={{
                            cursor:
                              item?.status === "signed" && item.editable
                                ? "pointer"
                                : "auto",
                            color:
                              item?.status === "signed"
                                ? "primary.main"
                                : "text.primary",
                          }}
                        >
                          {item?.status === "provider_signed" ||
                          item?.status === "verified"
                            ? "Signed"
                            : item?.status === "signed" && item.editable
                            ? formType === "consent"
                              ? "Sign Consent"
                              : "Sign Form"
                            : "-"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>{item?.version}</StyledTableCell>
                      <StyledTableCell>{item?.consentDate}</StyledTableCell>
                      <StyledTableCell>
                        <StatusBadge
                          label={
                            statusMap[item?.status]?.label ?? item?.status
                          }
                          variant={statusMap[item?.status]?.variant}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {downloadingRowId === item?.id &&
                        selectedParticipantId === item?.participantId ? (
                          <CircularProgress size={18} sx={{ ml: 1 }} />
                        ) : item?.status === "signed" ||
                          item?.status === "provider_signed" ||
                          item?.status === "verified" ? (
                          <IconButton
                            onClick={() =>
                              handleDownload(item?.participantId, item?.id)
                            }
                          >
                            <DownloadIcon />
                          </IconButton>
                        ) : item.editable ? (
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: "regular",
                              color: "primary.main",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              resendConsent(item?.participantId, item?.id)
                            }
                          >
                            Remind Again
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {(item?.status === "provider_signed" ||
                          item?.status === "signed") &&
                          item.editable && (
                            <IconButton
                              onClick={(e) => handleMenuClick(e, item)}
                            >
                              <MoreVert />
                            </IconButton>
                          )}
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
          {!loading && data.length === 0 && (
            <NoDataContainer>
              <Typography variant="body1" color="gray">
                No Data
              </Typography>
            </NoDataContainer>
          )}
          {loading && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
        </DataTableLayout>
      </Box>

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem onClick={openVerifyModal}>
          <ListItemText>
            {formType === "consent" ? "Verify Consent" : "Verify Form"}
          </ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={filterEl}
        open={openFilter}
        onClose={closeFilterMenu}
        sx={{
          "& .MuiPaper-root": {
            width: 200,
          },
        }}
      >
        {Object.keys(statusMap).map((key) => (
          <MenuItem key={key} onClick={() => handleFilter(key)}>
            <ListItemIcon>
              <Radio checked={filter === key} />
            </ListItemIcon>
            <ListItemText>{statusMap?.[key]?.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={clearFilter}>Clear</MenuItem>
      </Menu>

      {showVerifyModal && (
        <VerifyConsentModal
          show={showVerifyModal}
          onClose={closeVerifyModal}
          data={selectedRow}
          refreshPage={refreshPage}
          participantIdForModal={selectedRow?.participantId}
          studyId={studyId}
          type="study"
          formType={formType}
        />
      )}
    </>
  );
};

export default ConsentsTracking;
